import React, { useCallback } from 'react';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { RightPanel } from '@atlassian/jira-navigation-system/src/index.tsx';
import {
	PreviewPanel as GlobalPreviewPanel,
	PreviewPanelHeader,
	PreviewPanelTitle,
	PreviewPanelBody,
} from '@atlassian/preview-panel';
import { PanelSplitter } from '@atlassian/navigation-system';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { usePreviewPanel } from './PreviewPanelProvider.tsx';
import type { PreviewPanelContent } from './types.tsx';

export interface PreviewPanelProps {
	width?: number;
	onOpen?: () => void;
	onClose?: () => void;
	content?: PreviewPanelContent;
	onWidthChange?: (width: number) => void;
}

const PreviewPanel = () => {
	const { formatMessage } = useIntl();
	const { isOpen, width: currentWidth, content, closePreviewPanel, setWidth } = usePreviewPanel();

	const onResizeEnd = useCallback(
		({ finalWidth }: { finalWidth: number }) => {
			setWidth(finalWidth);
			return finalWidth;
		},
		[setWidth],
	);

	if (!isOpen || !content || !content.body) {
		return null;
	}

	return (
		<UFOSegment name="preview-panel">
			<RightPanel width={currentWidth} testId="page-container-v2.common.ui.preview-panel">
				<>
					<JSErrorBoundary
						fallback="unmount"
						id="preview-panel-route-components"
						packageName="page-container"
					>
						<GlobalPreviewPanel testId="page-container-v2.common.ui.preview-panel.global-preview-panel">
							<PreviewPanelHeader url={content.url} onClose={closePreviewPanel}>
								<PreviewPanelTitle title={content.title} />
							</PreviewPanelHeader>
							<PreviewPanelBody>{content.body}</PreviewPanelBody>
						</GlobalPreviewPanel>
					</JSErrorBoundary>
					<PanelSplitter
						label={formatMessage({
							id: 'preview-panel.splitter.label',
							defaultMessage: 'Panel Splitter',
						})}
						onResizeEnd={onResizeEnd}
						testId="page-container-v2.common.ui.preview-panel.panel-splitter"
					/>
				</>
			</RightPanel>
		</UFOSegment>
	);
};

export default PreviewPanel;
